import React, {Component} from 'react';
import logotipo from '../../images/logotipo_dashbo_purple.svg';
import LoginAcceptingInvitation from "./LoginAcceptingInvitation";
import {LoginCover} from "./LoginCover";
import {APIClient} from "../../utils/APIClient";
import {isOk} from "../../utils/apiResponse";
import {LoadErrorState} from "../elements/LoadErrorState";
import {API_URL} from "../../settings/local_settings";
import {LocalStorage} from "../../utils/LocalStorage";
import {TermsAndConditions} from "./TermsAndConditions";
import {withRouterParams} from "../../utils/withRouterParams";
import { withTranslation } from 'react-i18next';
import {Loading} from "../Loading";
import {portalLanguageOf} from "../portal/portalLanguage";

/**
 * La salida «Crear mi agencia» es para quien YA entró con Google y todavía no tiene agencia: el caso
 * de Leo (29/09), a quien el login mandó acá sin preguntarle porque su Gmail tenía una invitación
 * pendiente. Sin sesión no se ofrece: el alta pide login, y el login con una invitación pendiente
 * vuelve acá, así que el enlace sería un bucle. Con agencia tampoco: el alta la rebotaría.
 *
 * Sin agencia, `logged_agency_status` queda en `"null"`: el login manda `None` y `localStorage`
 * lo guarda como texto.
 */
function canCreateOwnAgency() {
    if (!LocalStorage.hasUserData() || LocalStorage.fetchUserType() !== 'AGENCY_USER') return false;
    const agencyStatus = LocalStorage.fetchAgencyStatus();
    return !agencyStatus || agencyStatus === 'null' || agencyStatus === 'undefined';
}

class AcceptInvitationComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            agencyName: null,
            email: null,
            loading: true,
            // `null` = sin error. Antes, todo lo que no fuera `ERROR_ALREADY_ACCEPTED` ni `'ERROR'`
            // caía en el `else` -la rama de ÉXITO- y moría en `data.data.agency_name`, con el
            // "Aguarde" girando para siempre.
            loadError: null
        };

        this.fetchInvitation = this.fetchInvitation.bind(this);
        this.handleRetryLoad = this.handleRetryLoad.bind(this);
        this.handleCreateOwnAgency = this.handleCreateOwnAgency.bind(this);
    }

    componentDidMount() {
        this.fetchInvitation();
    }

    /**
     * El ORDEN de las ramas es el diseño, no una formalidad.
     *
     * La guarda NO puede escribirse como `status !== 'OK'` -> `/page-not-found`: con eso, un
     * microcorte le diría al usuario que su invitación no existe y lo mandaría a pedir que se la
     * reenvíen, que es peor que el crash que este fix viene a arreglar. Se conservan los dos
     * códigos que sabemos leer y todo el resto -- el `NETWORK_ERROR` sintético del `APIClient` y
     * cualquier código tipado que el backend agregue -- va al estado de error, que es honesto: no
     * sabemos si la invitación sirve porque no pudimos preguntarlo.
     */
    fetchInvitation() {
        new APIClient(API_URL + 'invitation/' + this.props.match.params.hash).call('GET')
            .then((data) => {
                // Las dos salidas de un enlace que ya no sirve van con `replace`: con `push`, el
                // «Volver» del navegador (y el de la página 404) regresaba al enlace muerto, que
                // volvía a redirigir, y el usuario quedaba en un bucle (card I5AwONZS, 29/09).
                if (data.status === 'ERROR_ALREADY_ACCEPTED') {
                    const target = LocalStorage.hasUserData() ? '/' : '/login';
                    this.props.history.replace(target);
                } else if (data.status === 'ERROR' && !(data.http_status >= 500)) {
                    // "No se encuentra la invitación": el backend contesta con el código por
                    // DEFECTO de `ResultError` (`core/commands/multiusers/get_invitation_data.py`),
                    // que `ErrorResponse` escribe como `status: 'ERROR'` con un 422. Es el único
                    // no-OK que sabemos leer como "este enlace no sirve".
                    //
                    // ⚠️ Un 5xx sin JSON (el 500 HTML de Django, un 502/503 de Cloud Run) TAMBIÉN
                    // llega como `status: 'ERROR'`, pero con su `http_status`: no dice que la
                    // invitación no exista, dice que no pudimos preguntarlo, y va al estado de error
                    // con «Reintentar». Tomarlo como enlace muerto armaba un bucle con sesión: acá
                    // `replace('/')`, `Default` vuelve a encontrar la invitación pendiente (que sí
                    // existe) y la abre otra vez, dos pedidos por vuelta durante todo el incidente
                    // (review C-registro-02).
                    //
                    // Con sesión no es un 404: es una invitación que la agencia BORRÓ. Pasó en la
                    // call con Leo (29/09): la app lo mandó a la invitación al entrar, fede la borró
                    // desde Usuarios y al recargar vio «Página no encontrada» (verificado en prod:
                    // el hash ya no existe). Quien tiene sesión va al inicio, que decide solo:
                    // sin agencia, «Crea tu agencia» (o la otra invitación que tenga pendiente);
                    // con agencia, su Tablero.
                    this.props.history.replace(LocalStorage.hasUserData() ? '/' : '/page-not-found');
                } else if (!isOk(data)) {
                    this.setState({loading: false, loadError: data.error_message || ''});
                } else {
                    this.applyInvitationLanguage(data.data.language);
                    this.setState({
                        agencyName: data.data.agency_name,
                        email: data.data.email,
                        loading: false,
                        loadError: null
                    });
                }
            });
    }

    handleRetryLoad() {
        this.setState({loading: true, loadError: null}, this.fetchInvitation);
    }

    /**
     * La invitación al portal trae el idioma con el que la agencia invitó (el mail ya llegó en él): la
     * pantalla le habla en ese idioma a quien todavía no entró. Con sesión no se toca: la pantalla está
     * en el idioma de quien ya entró, y al aceptar el login le aplica el suyo.
     */
    applyInvitationLanguage(rawLanguage) {
        const language = portalLanguageOf(rawLanguage);
        const i18n = this.props.i18n;
        if (!language || LocalStorage.hasUserData() || !i18n || typeof i18n.changeLanguage !== 'function') return;
        if (portalLanguageOf(i18n.resolvedLanguage || i18n.language) !== language) i18n.changeLanguage(language);
    }

    /**
     * «Crear mi agencia» sin aceptar la invitación (card I5AwONZS). Con `push`: si cambia de idea,
     * «Volver» lo trae de nuevo acá, y la invitación sigue pendiente.
     */
    handleCreateOwnAgency() {
        this.props.history.push('/agency-register');
    }

    /**
     * La columna del login SIEMPRE va firmada, en todos sus estados.
     *
     * La rama de bienvenida (`AcceptInvitationSidebar`) dibuja el logo; el error y la espera no lo
     * hacían, así que quien llega desde un correo de invitación se encontraba con una página sin marca
     * diciéndole que algo falló (QA-VIS-net-04). Lo de la espera era preexistente y va en el mismo
     * movimiento: si no, pasar de una a otra hace aparecer el logo de la nada.
     */
    renderSidebarState(contenido) {
        return (
            <div className="app-content">
                <div className="login-sidebar">
                    <img src={logotipo} className="login-logo" alt="Dashbo"/>
                    <div className="login-main">
                        <div className="login-panel">{contenido}</div>
                    </div>
                </div>
                <LoginCover/>
            </div>
        );
    }

    render() {
        const { t } = this.props;

        if (this.state.loadError !== null) {
            return this.renderSidebarState(
                <LoadErrorState title={t('login.invitation_load_error')}
                                text={t('login.invitation_load_error_explanation')}
                                retryLabel={t('login.invitation_load_error_retry')}
                                onRetry={this.handleRetryLoad}/>
            );
        }

        if (this.state.loading) {
            return this.renderSidebarState(<Loading message={t('login.wait')}/>);
        }

        return (
            <div className="app-content">
                <AcceptInvitationSidebar
                    hash={this.props.match.params.hash}
                    agencyName={this.state.agencyName}
                    email={this.state.email}
                    onCreateOwnAgency={canCreateOwnAgency() ? this.handleCreateOwnAgency : null}
                    t={t}
                />
                <LoginCover/>
            </div>
        );
    }
}

export default withRouterParams(withTranslation()(AcceptInvitationComponent));

export class AcceptInvitationSidebar extends Component {
    render() {
        const { t, agencyName } = this.props;
        return (
            <div className="login-sidebar invitation-sidebar">
                <img src={logotipo} className="login-logo" alt="Dashbo"/>
                <div className="login-main">
                    <div className="login-panel">
                        <div className="invitation-context">
                            <p className="invitation-title">{t('login.invitation_title')}</p>
                            <h1 className="invitation-agency-name">{agencyName}</h1>
                            <p className="invitation-description">{t('login.invitation_description')}</p>
                        </div>
                        <LoginAcceptingInvitation hash={this.props.hash} email={this.props.email}/>
                        {/* La otra puerta, debajo de la principal y sin competirle: quien llegó acá sin
                            buscarlo puede crear su propia agencia sin aceptar nada (card I5AwONZS). */}
                        {this.props.onCreateOwnAgency && (
                            <div className="invitation-create-own">
                                <p className="invitation-create-own-question">{t('accept_invitation.create_own')}</p>
                                <button type="button" className="invitation-create-own-link"
                                        onClick={this.props.onCreateOwnAgency}>
                                    {t('accept_invitation.create_own_cta')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <TermsAndConditions/>
            </div>
        );
    }
}
